/**
 * CharacterAvatar — single-responsibility renderer for a character sprite
 * with pixel-art treatment and expression-driven animation.
 *
 * Reads from `src/characters/manifest.ts` exclusively. Swapping artwork
 * is a manifest-only change; this component never needs to know about
 * individual character details.
 *
 * Layering (bottom → top):
 *   1. tinted disc (character hue @ ~12% alpha — frames the silhouette)
 *   2. neon ring (active turn = cyan pulse / winner = gold flash)
 *   3. pixel sprite (image-rendering: pixelated)
 *   4. optional expression-specific overlay (thinking wobble, winner
 *      flash filter) applied on the sprite layer
 */
import {
  type CharacterCodename,
  type CharacterExpression,
  getCharacterAsset,
  resolveSprite,
} from '@/characters/manifest';

export type CharacterAvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type CharacterAvatarProps = {
  codename: CharacterCodename;
  expression?: CharacterExpression;
  size?: CharacterAvatarSize;
  /** Neon-cyan ring + pulse (active turn). */
  active?: boolean;
  /** Gold flash (between-hands winner). */
  winner?: boolean;
  /** Dim + desaturate (folded seat). */
  muted?: boolean;
  /** Explicit override for breathing idle animation. Defaults: off while
   *  thinking / winner to avoid compounding transforms. */
  breathing?: boolean;
  /** Rendered testid — defaults to `character-<codename>`. */
  testId?: string;
  className?: string;
};

const SIZE: Record<CharacterAvatarSize, string> = {
  xs: 'w-10 h-10',
  sm: 'w-12 h-12',
  md: 'w-16 h-16',
  lg: 'w-24 h-24',
  xl: 'w-32 h-32',
};

export function CharacterAvatar({
  codename,
  expression = 'idle',
  size = 'md',
  active,
  winner,
  muted,
  breathing,
  testId,
  className = '',
}: CharacterAvatarProps) {
  const asset = getCharacterAsset(codename);
  const sprite = resolveSprite(asset, expression);
  const tint = `hsl(var(--${asset.glowVar}) / 0.14)`;

  // Ring — mutually exclusive states, priority: winner > active > default.
  // Winner uses gold drop-glow. Active leans on the shared `.pulse-ring`
  // keyframe so the ring actually pulses (matches M5 expectation). Default
  // gets a subtle inset border to carve the avatar out of dark felt.
  const ringStyle: React.CSSProperties = winner
    ? {
        boxShadow:
          '0 0 0 2px hsl(var(--color-warning)), 0 0 18px hsl(var(--color-warning) / 0.7), 0 0 30px hsl(var(--color-warning) / 0.35)',
      }
    : active
      ? {
          // Inset ring; the outer animated halo is handled by the
          // `.pulse-ring` class below.
          boxShadow: 'inset 0 0 0 2px hsl(var(--color-accent-alt))',
        }
      : {
          boxShadow: 'inset 0 0 0 1px hsl(var(--color-border))',
        };

  const shouldBreathe = breathing ?? (expression !== 'thinking' && !winner && !active);
  // Animation class — one at a time to keep transforms composable.
  // Priority: winner > thinking > active (pulse-ring) > idle-breathe.
  const animClass = winner
    ? 'winner-flash'
    : expression === 'thinking'
      ? 'thinking-wobble'
      : active
        ? 'pulse-ring'
        : shouldBreathe
          ? 'idle-breathe'
          : '';

  return (
    <div
      className={`relative ${SIZE[size]} ${muted ? 'opacity-40 grayscale' : ''} ${className}`}
      data-testid={testId ?? `character-${codename}`}
      data-expression={expression}
      data-active={active ? 'true' : undefined}
      data-winner={winner ? 'true' : undefined}
      data-muted={muted ? 'true' : undefined}
      role="img"
      aria-label={asset.displayName}
    >
      <div
        className={`absolute inset-0 rounded-pill overflow-hidden ${animClass}`}
        style={{ background: tint, ...ringStyle }}
      >
        <img
          src={sprite}
          alt=""
          draggable={false}
          className="w-full h-full pixelated select-none"
        />
      </div>
    </div>
  );
}
